/**
 * LA SCHEDA «LIVE» DELLA HUB CLASH ROYALE, sui tornei di Limulo: quali incontri sono in corso e
 * come si raggruppano, senza React (un banco la esegue sotto Node).
 *
 * Richiesta di Luca, I-224 (26/09): «ora devo vedere tutte le partite clash royale che sono live in
 * quel momento, suddivise per torneo di appartenenza (coda, sit, torneo, sfida)». Dal 05/10 (r11) senza la
 * sfida: tre famiglie e un filtro per ciascuna (`filtriLive.ts`).
 * Analisi e scelte: `/home/dev/.qbridge/scambio/cr-live-orcb-fase1.md`, voci L-2314 (quando e'
 * «live»), L-2315 (da dove si legge), L-2316 (cosa fa una riga).
 *
 * ═══ LE FAMIGLIE SONO TUTTE TORNEI qTOURNAMENTS ═══
 * Una partita di coda e' un torneo da 2 gia' pieno, un sit&go un torneo da 3-32, una sfida fra amici
 * un torneo da 2 non valutato: li crea il giro delle code (`giro-code.ts`, `sfide.ts`). Il motore
 * scrive la famiglia nel campo `origine` del torneo, e dalla lettura unica (lotto 2) la riga la porta
 * nel suo `tournament.origine`, filtrata dall'allowlist del BFF
 * (`manuale | coda | sit-and-go | sfida | community`, `live-matches.js` @ `qlash-limulo` staging
 * `4348dc6`). ⚠️ STORIA: fino a qlash-limulo#152 (27/09) il sanitizer NON portava `origine`, e l'unico
 * indizio era il prefisso dell'id (`coda-` / `sng-`, `code.ts:1131`). Il prefisso resta come ripiego
 * per `origine: null` — i tornei nati prima del campo, che il motore NON battezza «manuale» (@gionko,
 * kb#299).
 *
 * ═══ DA DOVE SI LEGGE (lotto 2, L-2315 a) ═══
 * Una chiamata per gioco CR, `GET /v1/tournaments/live-matches` (qlash-limulo#169 → capability
 * `tournament.match.live.list`, qTournaments 1.79.0): il motore filtra gia' gli incontri in corso,
 * diviso per modo come `inCorso` qui sotto (kb#299, 19:37Z). `inCorso` resta come GUARDIA: se il
 * bordo un giorno allargasse il filtro, la scheda non direbbe «si gioca» su un incontro che non lo e'.
 * Il lotto 1 leggeva a VENTAGLIO (tornei in corso, poi il tabellone di ciascuno): e' in #459.
 */
import type {
  LimuloLinkSpettatore,
  LimuloMatch,
  LimuloPartitaLive,
  LimuloPartiteLive,
  LimuloTorneoLive,
} from '../../types/limulo.ts';

import { externalMatchDeadOf } from './externalData.ts';
import { matchOf } from './model.ts';

/** Le famiglie che il motore sa dire: `famigliaDi` le distingue TUTTE, sfida compresa. */
export type FamigliaLive = 'coda' | 'sit-and-go' | 'torneo' | 'sfida';

/** Quelle che la Live MOSTRA: la sfida fra amici non c'e' (r11, Luca 05/10: «le amichevoli non devono entrarci»). */
export type FamigliaMostrata = Exclude<FamigliaLive, 'sfida'>;

/**
 * L'ordine delle sezioni a schermo. Una famiglia senza partite non si mostra.
 * ⛔ E' ANCHE l'elenco di cio' che la Live mostra, in un punto solo (`mostrata`, usata da `partiteLive`):
 * la sfida fra amici sta fuori da TUTTO — nessuna sezione, nessun filtro, nemmeno la MIA partita in testa
 * con «Vai» (alla sfida si arriva dal suo flusso). Rimetterla qui la rimette a schermo, e `famigliaDi`
 * continua a dire `sfida` apposta: senza, una sfida cadrebbe sotto «Tornei».
 */
export const FAMIGLIE_LIVE: readonly FamigliaMostrata[] = Object.freeze(['coda', 'sit-and-go', 'torneo']);

const mostrata = (f: FamigliaLive): f is FamigliaMostrata => (FAMIGLIE_LIVE as readonly FamigliaLive[]).includes(f);

/**
 * `origine` del motore → famiglia. ⚠️ Confronto ESATTO sui valori di `ORIGINI_TORNEO`.
 * Un valore ignoto NON inventa una famiglia: ricade sul prefisso dell'id, e poi su «torneo».
 * ⚠️ Un documento vecchio NON ha il campo [M qtournaments-orcb 2026-09-27 00:17Z: 5 tornei CR su 31
 * senza `origine`]: e' il motivo per cui l'assenza non e' un errore.
 */
const FAMIGLIA_DA_ORIGINE: Readonly<Record<string, FamigliaLive>> = Object.freeze({
  manuale: 'torneo',
  // Un torneo creato da un MEMBRO (qTournaments 1.83.0): per chi guarda e' un torneo come gli altri.
  community: 'torneo',
  coda: 'coda',
  'sit-and-go': 'sit-and-go',
  // La sfida fra amici (qTournaments 1.82.0, kb#296): un torneo da 2 con la sua origine.
  sfida: 'sfida',
});

/**
 * La famiglia di un torneo. `origine` se il bordo la porta, altrimenti il prefisso dell'id.
 * ⚠️ Nessun ripiego sul prefisso `sfida-`: la sfida arriva SEMPRE con la sua origine (la emette il
 * motore dalla 1.82.0 e il BFF la lascia passare), e un torneo manuale chiamato «Sfida» ha lo stesso
 * prefisso (`tournamentBusinessId`: lo slug del nome) — il prefisso lo metterebbe fra le sfide.
 */
export function famigliaDi(torneo: { tournamentId: string; origine?: string | null }): FamigliaLive {
  const origine = torneo.origine;
  if (typeof origine === 'string' && Object.hasOwn(FAMIGLIA_DA_ORIGINE, origine)) {
    return FAMIGLIA_DA_ORIGINE[origine]!;
  }
  const id = torneo.tournamentId;
  if (id.startsWith('coda-')) return 'coda';
  if (id.startsWith('sng-')) return 'sit-and-go';
  return 'torneo';
}

/**
 * Il nome della coda da cui e' nato un torneo di coda: il giro lo battezza «<nome coda> · <suffisso>»
 * (`giro-code.ts:86`). Senza il separatore resta il nome intero.
 * ⚠️ E' il RIPIEGO: la lettura unica porta `tournament.queueName`, che viene dal modello di coda.
 * Questo taglio del nome serve solo quando il bordo non lo manda.
 */
export function nomeCodaDi(nomeTorneo: string): string {
  const i = nomeTorneo.lastIndexOf(' · ');
  return i > 0 ? nomeTorneo.slice(0, i) : nomeTorneo;
}

/**
 * «Questo incontro si sta giocando adesso?» — L-2314 (a): da «entrambi pronti» al risultato.
 *
 * Il motore non ha uno stato «live», e lo dice («"in corso" e' un fatto del provider»,
 * `match.model.ts:103-111`). Il predicato usa solo campi che attraversano gia' il bordo
 * (`sanitizePublicMatch`):
 *  · `status === 'pending'`, non un bye, due parti, e nessun `result` — ne' referto ne' proposta:
 *    una proposta vuol dire che la partita e' stata giocata;
 *  · il check-in d'incontro, se richiesto, chiuso con `outcome: 'ready'`;
 *  · se l'incontro passa da un provider (Clash Royale): la partita esterna esiste e non e' morta
 *    (`conflict|failed|cancelled`). Il motore NON scrive gli stati aperti (`incontri.ts:2168-2190`),
 *    quindi «invitato» e «in battaglia» qui sono la stessa cosa: sono i pochi minuti dell'invito;
 *  · in modo `manual` (nessuna partita esterna) l'unico segnale di «pronti» e' il check-in d'incontro
 *    chiuso `ready`. Un incontro SENZA check-in esce `required: false` — sia quando il torneo non lo
 *    chiede, sia se e' nato prima della 1.69 (`checkInDiIncontro`: `required: scadenza !== null`,
 *    qTournaments 1.76.0 `core/check-in-incontro.ts:231-241`) — e li' «pronti» non esiste: non e' live.
 * ⚠️ `resultMode` vuoto (bordo vecchio) senza partita esterna: NON e' live. Dire «si gioca» su un
 *    incontro di cui il server non ha detto il modo sarebbe inventarlo.
 */
export function inCorso(m: LimuloMatch): boolean {
  if (m.status !== 'pending' || m.isBye || m.result !== null) return false;
  if (m.playerA === '' || m.playerB === '') return false;
  const ci = m.matchCheckIn;
  if (ci && ci.required && ci.outcome !== 'ready') return false;
  if (m.externalMatch !== null) return !externalMatchDeadOf(m.externalMatch);
  // Senza partita esterna resta solo il modo `manual`, e li' il segnale di «pronti» e' UNO: il
  // check-in d'incontro chiuso `ready`. ⚠️ `required: false` NON lo e' (seconda passata della
  // revisione di #459): e' la forma di ogni incontro senza tempo, compresi quelli di prima della 1.69
  // in un torneo rimasto `in_progress`, che resterebbero «live» per sempre. Ne' lo e' un
  // `matchCheckIn` assente (bordo precedente a qlash-limulo#134).
  return m.resultMode === 'manual' && ci?.outcome === 'ready';
}

/** Da quando si gioca, in ms: `matchCheckIn.readyAt`, o `null` se il server non l'ha detto. */
export function inCorsoDa(m: LimuloMatch): number | null {
  const v = m.matchCheckIn?.readyAt;
  if (typeof v !== 'string') return null;
  const t = Date.parse(v);
  return Number.isFinite(t) ? t : null;
}

/**
 * Dove porta «Guarda». `web`: un URL http(s), si apre com'e'. `app`: il link del gioco
 * (`clashroyale://specHostedMatch?…`, cioe' la forma che il provider CR scrive,
 * `app-game-clash-royale/backend/spectator-links.js`), che la WebView di Telegram blocca e che
 * quindi passa dalla pagina ponte, come la Live di QDuels (`lib/crWatchLink.ts`).
 */
export interface LinkGuarda {
  readonly url: string;
  readonly tipo: 'web' | 'app';
}

export interface PartitaLive {
  readonly match: LimuloMatch;
  readonly torneo: LimuloTorneoLive;
  readonly famiglia: FamigliaMostrata;
  readonly da: number | null;
  /** Il link spettatore del PROVIDER, o `null`. ⛔ Mai costruito qui dal tag (ADR-0157 §7). */
  readonly guarda: LinkGuarda | null;
}

export interface GruppoLive {
  /** La chiave del gruppo: il torneo, o per le code il nome della coda. */
  readonly chiave: string;
  readonly nome: string;
  readonly partite: readonly PartitaLive[];
}

export interface SezioneLive {
  readonly famiglia: FamigliaMostrata;
  readonly gruppi: readonly GruppoLive[];
  readonly totale: number;
}

export interface Live {
  /** Le MIE partite in corso, in testa e una volta sola (come la Live backgammon, L-2298). Mai una sfida. */
  readonly mie: readonly PartitaLive[];
  /** Le altre, per famiglia nell'ordine di `FAMIGLIE_LIVE`; le famiglie vuote non ci sono. */
  readonly sezioni: readonly SezioneLive[];
  /** Tutte, mie comprese: e' il numero dell'intestazione. */
  readonly totale: number;
}

const mia = (m: LimuloMatch): boolean => m.players.a?.isMe === true || m.players.b?.isMe === true;

/** Piu' recente prima; un istante ignoto in fondo, senza rompere l'ordine degli altri. */
const piuRecente = (a: number | null, b: number | null): number =>
  a === null ? (b === null ? 0 : 1) : b === null ? -1 : b - a;

/** L'inizio esatto del link spettatore del provider CR (`spectator-links.js`): niente credenziali,
 * porta, percorso ne' host diverso possono stare fra lo schema e la query. */
const PREFISSO_LINK_GIOCO = 'clashroyale://specHostedMatch?';

/**
 * Il link di «Guarda» fra quelli del provider: il primo accettabile, dal lato A se c'e'.
 * Accettabili: un URL http(s) con un host e senza credenziali, oppure la forma del provider CR —
 * che comincia ESATTAMENTE con `PREFISSO_LINK_GIOCO`, porta `match_tag` e non ha frammento.
 * Qualunque altra cosa si scarta: un `javascript:` o uno schema qualsiasi passato alla pagina ponte
 * sarebbe un link aperto per conto del server senza che nessuno l'abbia deciso.
 * ⚠️ Il link del gioco si controlla sul TESTO, non con `new URL`: il prefisso esatto e' piu' stretto
 * (revisione di #488, punto 3), e non dipende da come un motore legge l'host di uno schema
 * personalizzato — [D, non misurato] le WebView Chromium piu' vecchie potrebbero non estrarlo.
 * ⚠️ Oggi il motore e il BFF lasciano passare solo http(s), mentre il provider scrive `clashroyale:`
 * ⇒ la lista arriva vuota e «Guarda» non compare (kb#299/kb#300, 28/09). Accettare qui le due forme
 * rende la scheda pronta a entrambe le soluzioni proposte, senza un'altra PR.
 */
export function linkGuardaDi(links: readonly LimuloLinkSpettatore[]): LinkGuarda | null {
  const ordinati = [...links].sort((x, y) => (x.side === y.side ? 0 : x.side === 'a' ? -1 : 1));
  for (const { url } of ordinati) {
    if (typeof url !== 'string' || url.length === 0 || url.length > 2048) continue;
    if (/[\s\p{Cc}\p{Cf}]/u.test(url)) continue;
    if (url.startsWith(PREFISSO_LINK_GIOCO)) {
      if (url.includes('#')) continue;
      if (new URLSearchParams(url.slice(PREFISSO_LINK_GIOCO.length)).get('match_tag')) {
        return { url, tipo: 'app' };
      }
      continue;
    }
    let u: URL;
    try {
      u = new URL(url);
    } catch {
      continue;
    }
    if ((u.protocol === 'https:' || u.protocol === 'http:') && u.hostname.length > 0 && !u.username && !u.password) {
      return { url, tipo: 'web' };
    }
  }
  return null;
}

const rec = (v: unknown): Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
const testo = (v: unknown): string => (typeof v === 'string' ? v : '');
const testoONull = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);

/**
 * La busta di `GET /v1/tournaments/live-matches` (il `data` di `{ ok, organizationId, data }`):
 * `{ items, total, page, limit, truncated }` (`sanitizeLiveMatches`, qlash-limulo `4348dc6`).
 * Una riga si scarta solo se non dice di quale torneo e' — ne' `tournament.tournamentId` ne' il suo
 * `tournamentId`: senza, il tocco non saprebbe cosa aprire. Con l'id ma senza il blocco `tournament`
 * resta, come torneo senza nome e senza origine (il motore oggi il blocco lo manda sempre, `live.js`).
 */
export function partiteLiveOf(data: unknown): LimuloPartiteLive {
  const d = rec(data);
  const items = Array.isArray(d.items) ? d.items : [];
  const partite: LimuloPartitaLive[] = [];
  for (const grezza of items) {
    const r = rec(grezza);
    const t = rec(r.tournament);
    const tournamentId = testo(t.tournamentId) || testo(r.tournamentId);
    if (tournamentId === '') continue;
    const links = (Array.isArray(r.spectatorLinks) ? r.spectatorLinks : []).flatMap((l) => {
      const x = rec(l);
      return (x.side === 'a' || x.side === 'b') && typeof x.url === 'string'
        ? [{ side: x.side, url: x.url } as LimuloLinkSpettatore]
        : [];
    });
    partite.push({
      match: matchOf(grezza),
      torneo: {
        tournamentId,
        name: testo(t.name),
        gameId: testo(t.gameId),
        origine: testoONull(t.origine),
        queueId: testoONull(t.queueId),
        queueName: testoONull(t.queueName),
      },
      spectatorLinks: links,
    });
  }
  const total = Number.isSafeInteger(d.total) && (d.total as number) >= 0 ? (d.total as number) : partite.length;
  return { partite, total, truncated: d.truncated === true };
}

/**
 * Dalle righe della lettura unica alla scheda. Qui si tengono solo gli incontri `inCorso` (guardia:
 * il motore li ha gia' filtrati) e solo le famiglie che la Live mostra.
 *  · Le code si raggruppano per CODA: ogni partita di coda e' un torneo a se', e un gruppo per
 *    partita non raggrupperebbe niente. La coda e' `queueId` se il bordo lo porta, altrimenti il nome.
 *  · Le sfide NON entrano: ne' in una sezione ne' fra le mie (`mostrata`, r11).
 *  · Sit&go e tornei: un gruppo per torneo.
 *  · Ordine: il gruppo con la partita piu' recente prima, e dentro il gruppo lo stesso.
 */
export function partiteLive(righe: readonly LimuloPartitaLive[]): Live {
  const mie: PartitaLive[] = [];
  const perFamiglia = new Map<FamigliaMostrata, Map<string, { nome: string; partite: PartitaLive[] }>>();
  const visti = new Set<string>();

  for (const { match, torneo, spectatorLinks } of righe) {
    const famiglia = famigliaDi(torneo);
    if (!mostrata(famiglia)) continue;
    const nomeTorneo = torneo.name || torneo.tournamentId;
    const nomeCoda = torneo.queueName ?? nomeCodaDi(nomeTorneo);
    const chiave = famiglia === 'coda' ? `coda:${torneo.queueId ?? nomeCoda}` : torneo.tournamentId;
    const nome = famiglia === 'coda' ? nomeCoda : nomeTorneo;

    const id = `${torneo.tournamentId}/${match.matchId}`;
    if (visti.has(id) || !inCorso(match)) continue;
    visti.add(id);
    const p: PartitaLive = {
      match,
      torneo,
      famiglia,
      da: inCorsoDa(match),
      guarda: linkGuardaDi(spectatorLinks),
    };
    if (mia(match)) {
      mie.push(p);
      continue;
    }
    let gruppi = perFamiglia.get(famiglia);
    if (!gruppi) perFamiglia.set(famiglia, (gruppi = new Map()));
    let g = gruppi.get(chiave);
    if (!g) gruppi.set(chiave, (g = { nome, partite: [] }));
    g.partite.push(p);
  }

  const sezioni: SezioneLive[] = [];
  for (const famiglia of FAMIGLIE_LIVE) {
    const gruppi = perFamiglia.get(famiglia);
    if (!gruppi) continue;
    const ordinati = [...gruppi.entries()]
      .map(([chiave, g]) => ({
        chiave,
        nome: g.nome,
        partite: [...g.partite].sort((a, b) => piuRecente(a.da, b.da)),
      }))
      .sort((a, b) => piuRecente(a.partite[0]?.da ?? null, b.partite[0]?.da ?? null));
    sezioni.push({ famiglia, gruppi: ordinati, totale: ordinati.reduce((n, g) => n + g.partite.length, 0) });
  }

  mie.sort((a, b) => piuRecente(a.da, b.da));
  return { mie, sezioni, totale: mie.length + sezioni.reduce((n, s) => n + s.totale, 0) };
}
